import i18next from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import { BCP47LangCode } from "./i18n.types";

/**
 * i18n configuration is sourced from the `i18n` field in package.json,
 * which acts as the single source of truth for supported languages.
 *
 * During the build step `generate:translations` aggregates all
 * `*.dictionary.<lang>.json` files into `__generated__/generated.<lang>.json`
 * bundles that are dynamically imported below.
 */
export const fallbackLng: BCP47LangCode = "en-US";

export const languages: BCP47LangCode[] = [
  "en-US",
  "en-CA",
  "en-GB",
  "es-MX",
  "fr-CA",
  "fr-FR",
  "de-DE",
  "it-IT",
  "ja-JP",
  "ko-KR",
  "vi-VN",
  "tl-PH",
  "zh-Hans",
  "zh-Hant",
];

export const defaultNS = "translation";

/**
 * The currently active language, readable outside a React component (e.g. from
 * router loaders / analytics identify calls). Falls back to the default before
 * i18next has resolved a language.
 */
export function getCurrentLanguage(): BCP47LangCode {
  return (i18next.resolvedLanguage as BCP47LangCode | undefined) ?? fallbackLng;
}

/**
 * Key under which the chosen language is persisted. We persist through the
 * app's `storage` module (Capacitor Preferences) rather than `localStorage`
 * so the preference survives on native iOS, where WebView localStorage can be
 * evicted.
 */
export const LANGUAGE_STORAGE_KEY = "i18nextLng";

const backend = resourcesToBackend(
  (language: string) =>
    import(
      /* webpackChunkName: "i18n-[request]" */
      `./__generated__/generated.${language}.json`
    ),
);

const baseInitOptions = {
  supportedLngs: languages,
  fallbackLng,
  fallbackNS: defaultNS,
  defaultNS,
  interpolation: {
    /** React already escapes values, so we disable i18next's escaping. */
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
};

let initPromise: Promise<void> | null = null;

export function initI18n() {
  if (i18next.isInitialized) {
    return Promise.resolve(i18next);
  }

  if (initPromise) {
    return initPromise.then(() => i18next);
  }

  // The storage-backed detector statically imports `@/modules/storage`; load it
  // lazily here (only in the real app) so importing this config module in unit
  // tests never pulls the storage singleton into the module graph.
  initPromise = import("./storageDetector")
    .then(({ storageLanguageDetector }) =>
      i18next
        .use(initReactI18next)
        .use(storageLanguageDetector)
        .use(backend)
        .init({
          ...baseInitOptions,
          lng: undefined, // Let the detector decide
        }),
    )
    .then(() => undefined);

  return initPromise.then(() => i18next);
}

/**
 * Test-only initializer: loads the real English dictionaries so components that
 * use `t()` render their English copy, but pins the language to en-US and omits
 * the storage-backed language detector. This keeps unit tests deterministic and
 * avoids mutating the shared `storage` singleton (which some tests assert on).
 */
export function initI18nForTests() {
  if (i18next.isInitialized) {
    return Promise.resolve(i18next);
  }

  return i18next
    .use(initReactI18next)
    .use(backend)
    .init({
      ...baseInitOptions,
      lng: fallbackLng,
    })
    .then(() => i18next);
}
